'use client';

import type { Collaborator } from '@/app/models/collaborator';
import type { Project } from '@/app/models/project';
import { SharingResultResponse } from '@/app/models/sharings';
import { useSearchSharingResultByQuery } from '@/app/services/sharingResultApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import formatterPercentualValue from '@/app/utils/percentualUtils';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import { TableCell } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HistoricTableProps {
  originProject: Project | null;
  destinationProject: Project | null;
  collaborator: Collaborator | null;
  period: string | null;
}

export default function HistoricTable({ originProject, destinationProject, collaborator, period }: HistoricTableProps) {
  const { t } = useTranslation(['historic', 'finalVersion', 'common']);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);

  const { data, isLoading: loading } = useSearchSharingResultByQuery(
    {
      originProjectSearchTerm: originProject?.name || '',
      destiniProjectSearchTerm: destinationProject?.name || '',
      personSearchTerm: collaborator?.name || '',
      period: period ? (DateTime.fromISO(`${period}`).toISODate() as string) : '',
      page,
      size: rowsPerPage,
    },
    { skip: !period },
  );

  const columns: Column[] = [
    { key: 'collaborator', label: t('finalVersion:collaborator') },
    { key: 'position', label: t('finalVersion:position') },
    { key: 'originProj', label: t('finalVersion:origin.proj') },
    { key: 'destinyProj', label: t('finalVersion:destiny.proj') },
    { key: 'sharing', label: t('finalVersion:sharing'), align: 'center' },
    { key: 'type', label: t('finalVersion:type'), align: 'center' },
    { key: 'value', label: t('finalVersion:value') },
  ];

  const renderRow = (row: SharingResultResponse) => (
    <>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip value={`${row.badge} - ${row.name}`} size={37} sx={bodyCellSx} />
      </TableCell>
      <TableCell sx={bodyCellSx}>{row.position}</TableCell>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip
          value={`${row.originProjectCode} - ${row.originProjectName}`}
          size={37}
          sx={bodyCellSx}
        />
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip
          value={`${row.destiniProjectCode} - ${row.destiniProjectName}`}
          size={37}
          sx={bodyCellSx}
        />
      </TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{`${formatterPercentualValue(row.percent)}`}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{row.type}</TableCell>
      <TableCell sx={bodyCellSx}>{`${formatterMonetaryValue(row.value)}`}</TableCell>
    </>
  );

  // Generate unique key using multiple fields
  const getRowKey = (row: SharingResultResponse) =>
    `${row.cpf}-${row.destiniProjectCode}-${row.originProjectCode}-${row.percent}`;

  const paginationConfig = {
    page,
    rowsPerPage,
    totalElements: data?.totalElements || 0,
    rowsPerPageOptions: [25, 50, 100, 150, 200],
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
    labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
      `${from}-${to} ${t('de')} ${count}`,
    labelRowsPerPage: t('common:itens.per.page'),
  };

  return (
    <DataTable
      columns={columns}
      data={data?.content || []}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
      pagination={paginationConfig}
      loading={loading}
      stickyHeader
      size="small"
    />
  );
}
